import React, { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import { computeSalaireBrutAnnuelFromMo, newStaffLine, parseNum } from './devisQuoteState';

const fmtEur = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    Number.isFinite(n) ? n : 0
  );

const DevisQuoteSalairesTab = ({
  salaires,
  qualifications,
  mainOeuvre,
  operationCategories,
  theme,
  patchSalaires,
  canEdit,
}) => {
  const inputStyle = {
    borderColor: theme.border,
    backgroundColor: theme.inputBackground,
    color: theme.text,
  };

  const quals = Array.isArray(qualifications) ? qualifications : [];

  const totalPersonnel = useMemo(() => {
    return salaires.staffLines.reduce((s, line) => {
      const n = parseInt(String(line.headcount).replace(/\s/g, ''), 10);
      return s + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0);
  }, [salaires.staffLines]);

  const salaireBrutAnnuel = useMemo(
    () =>
      computeSalaireBrutAnnuelFromMo(salaires, qualifications, mainOeuvre, operationCategories),
    [salaires, qualifications, mainOeuvre, operationCategories]
  );

  const prime = parseNum(salaires.primeObjectif, 400);
  const pct = parseNum(salaires.chargesSocialesPct, 32);
  const totalChargesSociales = (salaireBrutAnnuel + prime) * (pct / 100);

  const tr = parseNum(salaires.titreRestaurant, 0);
  const transp = parseNum(salaires.transport, 0);
  const totalAvantages = tr + transp;

  const totalAnnuelSalairesCharges = salaireBrutAnnuel + totalChargesSociales + totalAvantages;

  const hourLinesFor = (qualId) => {
    const q = quals.find((x) => String(x.id) === String(qualId));
    return Array.isArray(q?.hour_lines) ? q.hour_lines : [];
  };

  const updateLine = (lineId, patch) => {
    patchSalaires((prev) => ({
      ...prev,
      staffLines: prev.staffLines.map((l) => {
        if (l.id !== lineId) return l;
        const next = { ...l, ...patch };
        if (patch.qualificationId !== undefined) {
          const hl = hourLinesFor(next.qualificationId);
          const ok = hl.some((h) => String(h.id) === String(next.hourLineId));
          if (!ok) next.hourLineId = '';
        }
        return next;
      }),
    }));
  };

  const addLine = () => {
    patchSalaires((prev) => ({
      ...prev,
      staffLines: [...prev.staffLines, newStaffLine()],
    }));
  };

  const removeLine = (lineId) => {
    patchSalaires((prev) => {
      if (prev.staffLines.length <= 1) return prev;
      return { ...prev, staffLines: prev.staffLines.filter((l) => l.id !== lineId) };
    });
  };

  const lineInput = (val, onChange, opts = {}) => (
    <Input
      value={val}
      onChange={(e) => onChange(e.target.value)}
      disabled={!canEdit}
      className={opts.className || 'h-11 min-h-[44px] rounded-xl border-2 text-base tabular-nums sm:h-10 sm:text-sm touch-manipulation'}
      type={opts.type || 'text'}
      min={opts.min}
      step={opts.step}
      inputMode={opts.inputMode}
      placeholder={opts.placeholder}
      style={inputStyle}
    />
  );

  const selectClass =
    'h-11 min-h-[44px] w-full rounded-xl border-2 px-3 text-base sm:h-10 sm:text-sm touch-manipulation';

  return (
    <div className="space-y-5 min-w-0 sm:space-y-6">
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 px-4 py-4 shadow-sm"
        style={{ borderColor: theme.accent, backgroundColor: theme.accentSoft }}
      >
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Total annuel salaires & charges
          </p>
        </div>
        <p className="text-lg font-bold tabular-nums sm:text-xl shrink-0" style={{ color: theme.text }}>
          {fmtEur(totalAnnuelSalairesCharges)}
        </p>
      </div>

      <Card
        className="overflow-hidden rounded-2xl border shadow-sm"
        style={{ borderColor: theme.border, backgroundColor: theme.surface }}
      >
        <CardHeader
          className="border-b space-y-1.5 px-4 py-4 sm:px-5"
          style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
        >
          <p className="text-sm font-semibold sm:text-base" style={{ color: theme.text }}>
            Affectations poste / type d’horaire
          </p>
          <p className="text-xs leading-relaxed sm:text-sm" style={{ color: theme.textSecondary }}>
            Sélectionnez un poste puis un type d’horaire ; indiquez le nombre de personnes pour chaque
            ligne. Vous pouvez en ajouter plusieurs.
          </p>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="space-y-3">
            {salaires.staffLines.map((line) => {
              const hl = hourLinesFor(line.qualificationId);
              return (
                <div
                  key={line.id}
                  className="grid grid-cols-1 gap-3 rounded-xl border-2 p-3 sm:grid-cols-12 sm:items-end sm:gap-3 sm:p-4"
                  style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
                >
                  <div className="sm:col-span-4 space-y-1.5">
                    <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                      Poste
                    </Label>
                    <select
                      value={line.qualificationId}
                      onChange={(e) => updateLine(line.id, { qualificationId: e.target.value })}
                      disabled={!canEdit}
                      className={selectClass}
                      style={inputStyle}
                    >
                      <option value="">— Choisir un poste —</option>
                      {quals.map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.label || q.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-4 space-y-1.5">
                    <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                      Type d’horaire
                    </Label>
                    <select
                      value={line.hourLineId}
                      onChange={(e) => updateLine(line.id, { hourLineId: e.target.value })}
                      disabled={!canEdit || !line.qualificationId}
                      className={selectClass}
                      style={inputStyle}
                    >
                      <option value="">
                        {line.qualificationId ? '— Choisir un type —' : '— Choisir un poste d’abord —'}
                      </option>
                      {hl.map((h) => (
                        <option key={h.id} value={h.id}>
                          {(h.type_label || '').trim() || 'Type sans libellé'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-3 space-y-1.5">
                    <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                      Nombre de personnes
                    </Label>
                    {lineInput(
                      line.headcount,
                      (v) => updateLine(line.id, { headcount: v }),
                      { type: 'number', min: 0, step: 1, className: 'h-11 min-h-[44px] rounded-xl border-2 tabular-nums sm:h-10' }
                    )}
                  </div>
                  {canEdit && (
                    <div className="sm:col-span-1 flex justify-end pb-0.5 sm:pb-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 min-h-[44px] min-w-[44px] text-destructive hover:text-destructive touch-manipulation"
                        aria-label="Supprimer la ligne"
                        onClick={() => removeLine(line.id)}
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {canEdit && (
            <Button
              type="button"
              variant="secondary"
              className="h-11 min-h-[44px] w-full gap-2 rounded-xl font-semibold sm:h-10 sm:w-auto touch-manipulation"
              onClick={addLine}
            >
              <Plus className="h-4 w-4 shrink-0" />
              Ajouter une ligne
            </Button>
          )}

          <div
            className="mt-2 grid gap-3 rounded-xl border-2 p-4 sm:grid-cols-2"
            style={{ borderColor: theme.border, backgroundColor: theme.surface }}
          >
            <div className="space-y-1">
              <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                Total personnel
              </p>
              <p className="text-lg font-semibold tabular-nums" style={{ color: theme.text }}>
                {totalPersonnel}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                Salaire brut (annuel)
              </p>
              <p className="text-lg font-semibold tabular-nums" style={{ color: theme.text }}>
                {fmtEur(salaireBrutAnnuel)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card
        className="overflow-hidden rounded-2xl border shadow-sm"
        style={{ borderColor: theme.border, backgroundColor: theme.surface }}
      >
        <CardHeader
          className="border-b px-4 py-4 sm:px-5"
          style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
        >
          <p className="text-sm font-semibold sm:text-base" style={{ color: theme.text }}>
            Charges sociales
          </p>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                Prime sur objectif (budget annuel)
              </Label>
              {lineInput(
                salaires.primeObjectif,
                (v) => patchSalaires((p) => ({ ...p, primeObjectif: v })),
                { type: 'number', min: 0, step: 0.01, inputMode: 'decimal', className: 'h-11 min-h-[44px] rounded-xl border-2 tabular-nums sm:h-10' }
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                Charges sociales, taux de remplacement et marge masquée (%)
              </Label>
              {lineInput(
                salaires.chargesSocialesPct,
                (v) => patchSalaires((p) => ({ ...p, chargesSocialesPct: v })),
                { type: 'number', min: 0, step: 0.1, inputMode: 'decimal', className: 'h-11 min-h-[44px] rounded-xl border-2 tabular-nums sm:h-10' }
              )}
            </div>
          </div>
          <p className="text-xs leading-relaxed sm:text-sm" style={{ color: theme.textSecondary }}>
            Formule : (Σ salaires bruts + prime) × {pct.toLocaleString('fr-FR')}&nbsp;%
          </p>
          <div
            className="rounded-xl border-2 px-4 py-3"
            style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
          >
            <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>
              Total personnel — charges sociales annuelles
            </p>
            <p className="text-lg font-semibold tabular-nums" style={{ color: theme.text }}>
              {fmtEur(totalChargesSociales)}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card
        className="overflow-hidden rounded-2xl border shadow-sm"
        style={{ borderColor: theme.border, backgroundColor: theme.surface }}
      >
        <CardHeader
          className="border-b px-4 py-4 sm:px-5"
          style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
        >
          <p className="text-sm font-semibold sm:text-base" style={{ color: theme.text }}>
            Avantages
          </p>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                Titre restaurant
              </Label>
              {lineInput(
                salaires.titreRestaurant,
                (v) => patchSalaires((p) => ({ ...p, titreRestaurant: v })),
                { type: 'number', min: 0, step: 0.01, inputMode: 'decimal', className: 'h-11 min-h-[44px] rounded-xl border-2 tabular-nums sm:h-10' }
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                Transport
              </Label>
              {lineInput(
                salaires.transport,
                (v) => patchSalaires((p) => ({ ...p, transport: v })),
                { type: 'number', min: 0, step: 0.01, inputMode: 'decimal', className: 'h-11 min-h-[44px] rounded-xl border-2 tabular-nums sm:h-10' }
              )}
            </div>
          </div>
          <div
            className="rounded-xl border-2 px-4 py-3"
            style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
          >
            <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>
              Total avantages
            </p>
            <p className="text-lg font-semibold tabular-nums" style={{ color: theme.text }}>
              {fmtEur(totalAvantages)}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DevisQuoteSalairesTab;
